import React, { memo, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import AsyncSelect from "react-select/async";
import Select from "react-select";

import { Creators as NotificationsActions } from "./../../store/ducks/notifications";

import { isScopes } from "./../../services/Auth";
import { maskCPF } from "./../../utils/CPF";
import { maskCNPJ } from "./../../utils/CNPJ";
import { isValidForm } from "./../../utils/Form";
import { isEmpty, ucFirstAll } from "./../../utils/String";
import { clearObject, mergeObject } from "./../../utils/Object";

import ClientService from "./../../services/Client";
import ClientSchemaReduced from "./../../services/Client/SchemaReduced";
import DriverService from "./../../services/Driver";
import DriverSchema from "./../../services/Driver/Schema";
import WorkingWeekService from "./../../services/WorkingWeek";
import WorkingWeekSchemaReduced from "./../../services/WorkingWeek/SchemaReduced";

const Driver = ({
  options,
  props: { currentUser },
  funcs: { addNotice }
}) => {
  const { t } = useTranslation();
  const clientService = new ClientService();
  const driverService = new DriverService();
  const workingWeekService = new WorkingWeekService();

  const {
    driver: __driver = {},
    close: __close = () => {}
  } = options;

  const [driver, setDriver] = useState(mergeObject(JSON.parse(JSON.stringify(DriverSchema)), __driver));

  useEffect(() => {
    setTimeout(() => {
      if(!isScopes({ currentUser, scopes: [
        "is:master",
        "read:drivers:all"
      ], every: false })) {
        setClientSelectedOption({
          dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client),
          label: currentUser.client.type === "LEG" ? (`${currentUser.client.legal.socialReason} | CNPJ: ${maskCNPJ(currentUser.client.legal.cnpj)}`) : (`${currentUser.client.personal.name} | CPF: ${maskCPF(currentUser.client.personal.cpf)}`),
          value: currentUser.client.id
        });
        setDriver(prevState => ({
          ...prevState,
          client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), currentUser.client)
        }));
      }
    }, 1000);
  }, []); /* eslint-disable-line */

  const [activeOptions] = useState([
    { value: true, label: t("Active") },
    { value: false, label: t("Inactive") }
  ]);

  const [clientSelectedOption, setClientSelectedOption] = useState(driver.client.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), driver.client),
    label: driver.client.type === "LEG" ? (`${driver.client.legal.socialReason} | CNPJ: ${maskCNPJ(driver.client.legal.cnpj)}`) : (`${driver.client.personal.name} | CPF: ${maskCPF(driver.client.personal.cpf)}`),
    value: driver.client.id
  });

  const [workingWeekSelectedOption, setWorkingWeekSelectedOption] = useState(driver.workingWeek.id === "" ? null : {
    dataAux: mergeObject(JSON.parse(JSON.stringify(WorkingWeekSchemaReduced)), driver.workingWeek),
    label: driver.workingWeek.name,
    value: driver.workingWeek.id
  });

  const [running, setRunning] = useState(false);

  const buttonDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const clientLoadOptions = (e, c) => {
    clientLoadOptionsDebounce(e, c);
  };

  const clientLoadOptionsDebounce = debounce(async (e, c) => {
    const clients = await clientService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterClient(clients));
  }, 1000);

  const filterClient = (clients) => {
    return clients.map(e => {
      return {
        dataAux: e,
        label: e.type === "LEG" ? (`${e.legal.socialReason} | CNPJ: ${maskCNPJ(e.legal.cnpj)}`) : `${e.personal.name} | CPF: ${maskCPF(e.personal.cpf)}`,
        value: e.id
      };
    });
  };

  const workingWeekLoadOptions = (e, c) => {
    workingWeekLoadOptionsDebounce(e, c);
  };

  const workingWeekLoadOptionsDebounce = debounce(async (e, c) => {
    const workingWeeks = await workingWeekService.findAutocomplete({ data: {
      search: e
    }, token: currentUser.token });
    c(filterWorkingWeek(workingWeeks));
  }, 1000);

  const filterWorkingWeek = (workingWeeks) => {
    return workingWeeks.map(e => {
      return {
        dataAux: e,
        label: e.name,
        value: e.id
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if(!running) {
      setRunning(true);
      let { valid, message } = isValidForm(e);
      if(!valid) {
        if(!message) {
          message = t("Error.Fields.Empty");
        }
        addNotice({
          title: t("Title.Driver"),
          content: message
        });
      }
      else {
        let response = false;
        if(isCreate()) {
          response = await driverService.create({
            data: handleSubmitForm({ driver }),
            token: currentUser.token
          });
        }
        else {
          response = await driverService.update({
            id: driver.id,
            data: handleSubmitForm({ driver }),
            token: currentUser.token
          });
        }

        if(response.success) {
          addNotice({
            title: t("Title.Driver"),
            content: isCreate() ? t("Success.Insert") : t("Success.Update")
          });
          setTimeout(() => {
            __close();
          }, 200);
        }
        else {
          addNotice({
            title: t("Title.Driver"),
            content: t(`Error.Driver.${response.error.type}.${ucFirstAll(response.error.details[0].path)}`)
          });
        }
      }
    }
    setRunning(false);
  };

  const handleSubmitForm = ({ driver }) => {
    let newDriver = Object.assign({}, JSON.parse(JSON.stringify(driver)));
    newDriver = clearObject({ data: newDriver });
    newDriver.id = undefined;
    return newDriver;
  };

  const inputDisabled = () => {
    if(!running) {
      return false;
    }
    return true;
  };

  const isCreate = () => {
    return driver.id === "";
  };

  return (
    <Form id="forms-driver" noValidate onSubmit={handleSubmit}>
      <Card>
        <Card.Header>{t("Title.GeneralData")}</Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-driver-active">
                <Form.Label>{t("Label.Active")}:</Form.Label>
                <Select
                  className="menu-outer-bottom"
                  classNamePrefix="select"
                  isDisabled={inputDisabled()}
                  onChange={e => {
                    const active = e.value;
                    setDriver(prevState => ({
                      ...prevState,
                      active
                    }));
                  }}
                  options={activeOptions}
                  value={activeOptions.find(x => x.value === driver.active)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-user-client">
                <Form.Label>{t("Title.Client")}:</Form.Label>
                <AsyncSelect
                  className={`menu-outer-bottom ${!isEmpty(driver.client.id) ? "is-valid" : "is-invalid"}`}
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  isDisabled={inputDisabled() || !isScopes({
                    currentUser,
                    scopes: [
                      "is:master",
                      "read:drivers:all"
                    ],
                    every: false
                  })}
                  loadOptions={clientLoadOptions}
                  loadingMessage={() => t("React.Select.Wait")}
                  noOptionsMessage={() => t("React.Select.NoOptions")}
                  onChange={e => {
                    if(e === null) {
                      setClientSelectedOption(e);
                      setDriver(prevState => ({
                        ...prevState,
                        client: JSON.parse(JSON.stringify(ClientSchemaReduced))
                      }));
                    }
                    else {
                      const { dataAux, label, value } = e;
                      setClientSelectedOption({ dataAux, label, value });
                      setDriver(prevState => ({
                        ...prevState,
                        client: mergeObject(JSON.parse(JSON.stringify(ClientSchemaReduced)), dataAux)
                      }));
                    }
                  }}
                  placeholder={t("Title.Client.Select")}
                  value={clientSelectedOption}
                  required={true}
                />
                <Form.Control.Feedback className={`${!isEmpty(driver.client.id) ? "hide" : "show"}`} type="invalid">{t("Feedback.Client")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-driver-name">
                <Form.Label>{t("Label.Name")}:</Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t("Placeholder.Name")}
                  disabled={inputDisabled()}
                  onChange={e => {
                    const name = e.target.value;
                    setDriver(prevState => ({
                      ...prevState,
                      name: name.toUpperCase()
                    }));
                  }}
                  autoComplete="off"
                  value={driver.name}
                  isValid={!isEmpty(driver.name)}
                  isInvalid={isEmpty(driver.name)}
                  required={true}
                />
                <Form.Control.Feedback type="invalid">{t("Feedback.Name")}</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group controlId="forms-driver-working-week">
                <Form.Label>{t("Title.WorkingWeek")}:</Form.Label>
                <AsyncSelect
                  className="menu-outer-bottom"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  isClearable
                  isDisabled={inputDisabled()}
                  loadOptions={workingWeekLoadOptions}
                  loadingMessage={() => t("React.Select.Wait")}
                  noOptionsMessage={() => t("React.Select.NoOptions")}
                  onChange={e => {
                    if (e === null) {
                      setWorkingWeekSelectedOption(e);
                      setDriver(prevState => ({
                        ...prevState,
                        workingWeek: JSON.parse(JSON.stringify(WorkingWeekSchemaReduced))
                      }));
                    }
                    else {
                      const { dataAux, label, value } = e;
                      setWorkingWeekSelectedOption({ dataAux, label, value });
                      setDriver(prevState => ({
                        ...prevState,
                        workingWeek: mergeObject(JSON.parse(JSON.stringify(WorkingWeekSchemaReduced)), dataAux)
                      }));
                    }
                  }}
                  placeholder={t("Title.WorkingWeek.Select")}
                  value={workingWeekSelectedOption}
                />
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Form.Group className="default-form-button">
        <Button
          variant="dark"
          type="submit"
          disabled={buttonDisabled()}
        >
          {running ? <Spinner animation="border" size="sm" /> : t("Button.Save")}
        </Button>
      </Form.Group>
    </Form>
  );
};

const mapStateToProps = state => ({
  props: {
    currentUser: state.users.currentUser
  }
});

const mapDispatchToProps = dispatch => ({
  funcs: bindActionCreators(Object.assign({}, NotificationsActions), dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(Driver));
